import { useCallback, useSyncExternalStore } from "react";

/** Phones and narrow windows: the width below which the shell becomes a top bar and panels open as sheets. Keep in step with styles.css. */
export const NARROW = "(max-width: 820px)";

/** Whether the media query matches, following changes (rotating a phone, resizing a window). */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback((change: () => void) => {
    const list = window.matchMedia(query);
    list.addEventListener("change", change);
    return () => list.removeEventListener("change", change);
  }, [query]);
  return useSyncExternalStore(subscribe, () => window.matchMedia(query).matches, () => false);
}

/** The shell's narrow layout. */
export const useNarrow = () => useMediaQuery(NARROW);

/**
 * A ref for `<table className="table stack-mobile">`: copies each column's header into its cells'
 * `data-label`, so that on narrow screens the CSS can show every row as a card of "label: value" lines.
 * Rows that arrive or change later are labelled too. A cell whose header is empty (actions) gets no label.
 */
export function stackOnMobile(table: HTMLTableElement | null): void | (() => void) {
  if (!table) return;
  const label = () => {
    const heads = [...table.querySelectorAll<HTMLTableCellElement>("thead tr:last-child th")].map((th) => th.textContent?.trim() ?? "");
    for (const row of table.querySelectorAll<HTMLTableRowElement>("tbody tr")) {
      let col = 0;
      for (const cell of row.cells) {
        const text = heads[col] ?? "";
        if (cell.dataset.label !== text) {
          if (text) cell.dataset.label = text;
          else delete cell.dataset.label;
        }
        col += cell.colSpan || 1;
      }
    }
  };
  label();
  const observer = new MutationObserver(label);
  observer.observe(table, { childList: true, subtree: true });
  return () => observer.disconnect();
}
